import { Stop, StopEta } from "@api/graphql/generated/generated-types";
import DriverTask from "@src/common/components/DriverTask/DriverTask";
import { Checkbox } from "@src/common/components";
import classnames from "classnames";
import { useDriverRouteViewSelectionProvider } from "../contexts/DriverRouteViewSelectionProvider";

export interface TaskStopCardSelection {
  isSelectionEnabled: boolean;
  isSelectionMode: boolean;
  isSelected: boolean;
  // Selected because a sibling stop of the same order was checked, not by a
  // click on this card. Rendered subdued so the dispatcher sees what the
  // order pulled in with it.
  isAutoSelected?: boolean;
  onToggle: (extendRange: boolean) => void;
}

export const TaskStopCardComponent = ({
  task,
  index,
  eta,
  stop,
  highlightedShipmentIds,
  selectedStopDate,
  selection,
}: {
  task: any;
  index: number;
  eta?: StopEta;
  stop?: Stop;
  highlightedShipmentIds: string[];
  selectedStopDate: string | undefined;
  selection?: TaskStopCardSelection;
}) => {
  const { hoveredTask, setHoveredTask, setHoverTaskLabel } =
    useDriverRouteViewSelectionProvider();
  const isHovered = hoveredTask?.id === task.id;
  const isSelected = !!selection?.isSelected;
  const isAutoSelected = !!selection?.isAutoSelected;
  const isSelectionEnabled = !!selection?.isSelectionEnabled;
  const isSelectionMode = !!selection?.isSelectionMode;
  // The sequence bubble becomes a checkbox on hover, so selecting a stop is
  // one click without a mode to turn on first.
  const showCheckbox = isSelectionEnabled && (isHovered || isSelected);

  const stopShipmentId: string | undefined = task.stop?.shipment.id || stop?.shipmentId;

  const bgColorClass = () => {
    if (isSelected && isAutoSelected)
      // Subdued version of the selected style: this stop rides along with a
      // checked sibling of its order rather than being checked itself.
      return "bg-primary-50 dark:bg-primary-900/20 border-dashed border-primary-300 dark:border-primary-700";
    if (isSelected)
      return "bg-primary-100 dark:bg-primary-900/40 border-primary-500 dark:border-primary-400";
    if (isHovered) return "bg-blue-300 dark:bg-blue-700";
    if (stopShipmentId && highlightedShipmentIds.includes(stopShipmentId))
      return "bg-blue-100 dark:bg-blue-900/30 border-primary-300 dark:border-primary-600";
    return "bg-white dark:bg-gray-800";
  };

  const renderCheckbox = () => (
    <div
      className="flex items-center justify-center h-full w-full"
      onClick={(event) => {
        event.stopPropagation();
        selection?.onToggle(event.shiftKey);
      }}
    >
      <Checkbox
        className={classnames("cursor-pointer", {
          "opacity-60": isAutoSelected,
        })}
        checked={isSelected}
        onChange={() => undefined}
        aria-label={`Select stop ${index + 1}`}
        title={
          isAutoSelected
            ? "Selected with the rest of its order — an order's stops move together"
            : "Shift-click to select a range"
        }
      />
    </div>
  );

  return (
    <div
      className={classnames(
        "mb-2 px-2 rounded-md shadow-sm border-2 dark:border-gray-700",
        isSelectionMode ? "cursor-pointer" : "cursor-move",
        bgColorClass()
      )}
      key={index}
      onMouseEnter={() => {
        setHoveredTask(task);
        setHoverTaskLabel(index + 1);
      }}
      onMouseLeave={() => {
        setHoveredTask(undefined);
        setHoverTaskLabel(undefined);
      }}
      onClick={(event) => {
        if (!isSelectionMode) return;
        selection?.onToggle(event.shiftKey);
      }}
    >
      <DriverTask
        task={task.__typename === "Task" ? task : undefined}
        key={task.id || stop?.id}
        index={index}
        showIndex={true}
        stopEta={eta}
        stop={task.__typename === "Task" ? undefined : stop}
        selectedStopDate={selectedStopDate}
        leadingContent={showCheckbox ? renderCheckbox() : undefined}
      />
    </div>
  );
};
